import { format, parseISO } from 'date-fns';
import { IntegracaoExternaDTO } from '../../shared/dto/integracaoExternaDTO';

export type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'secondary';

export interface FormattedField {
  label: string;
  value: string | number | boolean | null;
  variant?: BadgeVariant;
}

export interface FormattedSection {
  title: string;
  fields: FormattedField[];
}

export interface FormattedIntegration {
  tipo: string;
  tipoLabel: string;
  dataHora: string;
  gridSummary: string;
  status?: {
    label: string;
    variant: BadgeVariant;
  };
  sections: FormattedSection[];
  rawRequest?: string;
  rawResponse?: string;
}

export function safeJsonParse<T>(json: string | null | undefined): T | null {
  if (!json || !json.trim()) return null;
  try {
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

function safeFormatTimestamp(ts: number): string {
  try {
    const ms = String(ts).length <= 10 ? ts * 1000 : ts;
    return format(new Date(ms), 'dd/MM/yyyy');
  } catch {
    return String(ts);
  }
}

function safeFormatIso(iso: string): string {
  try {
    return format(parseISO(iso), 'dd/MM/yyyy');
  } catch {
    return iso;
  }
}

export function formatDate(value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'number') {
    return safeFormatTimestamp(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '—';
    if (/^-?\d+$/.test(trimmed)) {
      return safeFormatTimestamp(Number(trimmed));
    }
    return safeFormatIso(trimmed);
  }
  return String(value);
}

export function formatIntegration(dto: IntegracaoExternaDTO): FormattedIntegration {
  const request = safeJsonParse<unknown>(dto.request);
  const response = safeJsonParse<unknown>(dto.response);

  switch (dto.tipo) {
    case '9':
      return formatPep(dto, request, response);
    case '5':
      return formatSerasa(dto, request, response);
    case '2':
      return formatProcessosJuridicos(dto, request, response);
    case '7':
      return formatReceitaFederal(dto, request, response);
    case '8':
      return formatPh3aCheckup(dto, request, response);
    default:
      return formatGeneric(dto, request, response);
  }
}

function baseFormatted(dto: IntegracaoExternaDTO): FormattedIntegration {
  return {
    tipo: dto.tipo,
    tipoLabel: dto.tipoLabel || dto.tipo || 'Desconhecido',
    dataHora: dto.dataHora,
    gridSummary: 'Resumo indisponível',
    sections: [],
    rawRequest: dto.request,
    rawResponse: dto.response,
  };
}

function field(label: string, value: unknown, variant?: BadgeVariant): FormattedField {
  return { label, value: value === undefined || value === null ? '—' : String(value), variant };
}

function booleanField(label: string, value: unknown): FormattedField {
  const str = String(value || '').toLowerCase();
  const isTrue = value === true || str === 'true' || str === 'sim';
  const isFalse = value === false || str === 'false' || str === 'não' || str === 'nao';
  if (isTrue) return { label, value: 'Sim', variant: 'success' };
  if (isFalse) return { label, value: 'Não', variant: 'secondary' };
  return { label, value: String(value ?? '—') };
}

function riscoVariant(nivel: string): BadgeVariant {
  const nivelLower = String(nivel || '').toLowerCase();
  if (nivelLower.includes('alto')) return 'danger';
  if (nivelLower.includes('médio') || nivelLower.includes('medio') || nivelLower.includes('média')) return 'warning';
  if (nivelLower.includes('baixo') || nivelLower.includes('nenhum')) return 'success';
  return 'info';
}

function statusVariant(status: string): BadgeVariant {
  const statusLower = String(status || '').toLowerCase();
  if (statusLower.includes('sucesso') || statusLower.includes('ok') || statusLower.includes('regular') || statusLower.includes('aprovado')) {
    return 'success';
  }
  if (statusLower.includes('erro') || statusLower.includes('falha') || statusLower.includes('reprovado')) {
    return 'danger';
  }
  if (statusLower.includes('alerta') || statusLower.includes('pendente')) {
    return 'warning';
  }
  return 'info';
}

function section(title: string, fields: FormattedField[]): FormattedSection {
  return { title, fields };
}

function gridSummary(texto: string, limite = 80): string {
  const limpo = texto.replace(/\s+/g, ' ').trim();
  return limpo.length > limite ? limpo.substring(0, limite) + '...' : limpo;
}

function get(obj: any, ...paths: string[]): any {
  for (const path of paths) {
    const value = path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
}

function formatPep(
  dto: IntegracaoExternaDTO,
  request: unknown,
  response: unknown,
): FormattedIntegration {
  const base = baseFormatted(dto);
  if (!response) return base;
  const resp = response as any;
  const status = String(get(resp, 'status') || '—');
  const mensagem = String(get(resp, 'mensagem') || '');
  const dados = Array.isArray(resp?.dados) ? resp.dados : [];
  const constaBase = dados.length > 0;

  base.status = { label: status, variant: statusVariant(status) };

  const resumoParts = [`Status: ${status}`];
  if (constaBase) {
    resumoParts.push(`Consta na base: Sim (${dados.length} registro${dados.length > 1 ? 's' : ''})`);
  } else {
    resumoParts.push('Consta na base: Não');
  }
  if (mensagem) resumoParts.push(mensagem);
  base.gridSummary = gridSummary(resumoParts.join(' — '));

  const sections: FormattedSection[] = [
    section('Status', [
      field('Status', status, statusVariant(status)),
      field('Mensagem', mensagem || '—'),
      booleanField('Consta na base PEP', constaBase),
    ]),
  ];

  if (dados.length > 0) {
    sections.push(...dados.map((item: any, index: number) =>
      section(`Registro ${index + 1}`, [
        field('Nome', get(item, 'nomePEP')),
        field('CPF', get(item, 'cpf')),
        field('Órgão', get(item, 'nomeOrgao')),
        field('Função', get(item, 'descricaoFuncao') || get(item, 'siglaFuncao')),
        field('Nível', get(item, 'nivelFuncao')),
        field('Início', formatDate(get(item, 'dataInicioExercicio'))),
        field('Fim', formatDate(get(item, 'dataFimExercicio'))),
        field('Fim carência', formatDate(get(item, 'dataFimCarencia'))),
        field('Competência', formatDate(get(item, 'competencia'))),
      ])
    ));
  }

  base.sections = sections;
  return base;
}

function formatSerasa(
  dto: IntegracaoExternaDTO,
  request: unknown,
  response: unknown,
): FormattedIntegration {
  const base = baseFormatted(dto);
  if (!response) return base;
  const resp = response as any;
  const resumo = resp?.resumo_geral || {};
  const conclusao = resp?.conclusao_credito || {};

  const nivelRisco = String(get(resumo, 'nivel_risco') || '—');
  const resultado = String(get(resumo, 'resultado_serasa') || '—');
  const apto = get(conclusao, 'apto_para_emprestimo');

  base.status = { label: resultado, variant: statusVariant(resultado) };

  const resumoParts = [`Resultado: ${resultado}`];
  if (nivelRisco !== '—') resumoParts.push(`Risco: ${nivelRisco}`);
  base.gridSummary = gridSummary(resumoParts.join(' — '));

  base.sections = [
    section('Resumo Serasa', [
      field('Resultado', resultado, statusVariant(resultado)),
      field('Nível de risco', nivelRisco, riscoVariant(nivelRisco)),
      booleanField('Possui risco de crédito', get(resumo, 'possui_risco_credito')),
      field('Rank Serasa', get(resumo, 'rank_serasa')),
    ]),
    section('Conclusão de Crédito', [
      booleanField('Apto para empréstimo', apto),
      field('Observação', get(conclusao, 'observacao')),
    ]),
  ];

  return base;
}

function formatProcessosJuridicos(
  dto: IntegracaoExternaDTO,
  request: unknown,
  response: unknown,
): FormattedIntegration {
  const base = baseFormatted(dto);
  if (!response) return base;
  const resp = response as any;
  const resumo = resp?.resumo_geral || {};
  const processos = Array.isArray(resp?.detalhamento_processos) ? resp.detalhamento_processos : [];

  const nivelRisco = String(get(resumo, 'nivel_risco') || '—');
  const qtd = Number(get(resumo, 'qtd_processos') || 0);
  const riscoIdentificado = String(get(resumo, 'risco_credito_identificado') || 'NÃO');

  base.status = { label: riscoIdentificado === 'SIM' ? 'Risco identificado' : 'Sem risco', variant: riscoIdentificado === 'SIM' ? 'danger' : 'success' };

  const resumoParts = [`${qtd} processo${qtd !== 1 ? 's' : ''}`];
  if (nivelRisco !== '—') resumoParts.push(`Risco: ${nivelRisco}`);
  base.gridSummary = gridSummary(resumoParts.join(' — '));

  const sections: FormattedSection[] = [
    section('Resumo', [
      field('Quantidade de processos', qtd || '—'),
      field('Nível de risco', nivelRisco, riscoVariant(nivelRisco)),
      booleanField('Risco de crédito identificado', riscoIdentificado === 'SIM'),
      field('Ativos', get(resumo, 'ativos')),
      field('Encerrados', get(resumo, 'encerrados')),
      field('Outros', get(resumo, 'outros')),
    ]),
  ];

  if (processos.length > 0) {
    sections.push(...processos.map((item: any, index: number) =>
      section(`Processo ${index + 1}`, [
        field('Número', get(item, 'numero_processo')),
        field('Tribunal', get(item, 'tribunal')),
        field('Classe processual', get(item, 'classe_processual')),
        field('Status', get(item, 'status')),
        field('Risco', get(item, 'risco'), riscoVariant(get(item, 'risco'))),
        field('Impacto', get(item, 'impacto')),
      ])
    ));
  }

  base.sections = sections;
  return base;
}

function formatReceitaFederal(
  dto: IntegracaoExternaDTO,
  request: unknown,
  response: unknown,
): FormattedIntegration {
  const base = baseFormatted(dto);
  if (!response) return base;
  const resp = response as any;
  const resumo = resp?.resumo_geral || {};
  const conclusao = resp?.conclusao_credito || {};

  const situacao = String(get(resumo, 'situacao_cadastral') || '—');
  const nivelRisco = String(get(resumo, 'nivel_risco') || '—');
  const nome = String(get(resumo, 'nome') || '—');

  base.status = { label: situacao, variant: statusVariant(situacao) };

  const resumoParts = [`Situação: ${situacao}`];
  if (nivelRisco !== '—') resumoParts.push(`Risco: ${nivelRisco}`);
  base.gridSummary = gridSummary(resumoParts.join(' — '));

  base.sections = [
    section('Dados Cadastrais', [
      field('Nome', nome),
      field('CPF', get(resumo, 'cpf')),
      field('Situação cadastral', situacao, statusVariant(situacao)),
      field('Nível de risco', nivelRisco, riscoVariant(nivelRisco)),
      field('Data de nascimento', formatDate(get(resumo, 'data_nascimento'))),
    ]),
    section('Conclusão de Crédito', [
      booleanField('Apto para empréstimo', get(conclusao, 'apto_para_emprestimo')),
      field('Observação', get(conclusao, 'observacao')),
    ]),
  ];

  return base;
}

function formatPh3aCheckup(
  dto: IntegracaoExternaDTO,
  request: unknown,
  response: unknown,
): FormattedIntegration {
  const base = baseFormatted(dto);
  if (!response) return base;
  const resp = response as any;
  const data = resp?.data || {};
  const status = String(get(resp, 'status') || '—');
  const fiscal = data.fiscalSituation || {};
  const creditScore = data.creditScore || {};

  base.status = { label: status, variant: statusVariant(status) };

  const situacaoFiscal = String(get(fiscal, 'description') || '—');
  const resumoParts = [`Status: ${status}`];
  if (situacaoFiscal !== '—') resumoParts.push(`Situação fiscal: ${situacaoFiscal}`);
  base.gridSummary = gridSummary(resumoParts.join(' — '));

  base.sections = [
    section('Dados Pessoais', [
      field('Nome', get(data, 'nameBrasil')),
      field('CPF', get(data, 'document')),
      field('Idade', get(data, 'age')),
      field('Gênero', get(data, 'gender')),
      field('Data de nascimento', formatDate(get(data, 'birthDate'))),
      field('Dependentes', get(data, 'person.dependents')),
    ]),
    section('Situação Fiscal', [
      field('Descrição', situacaoFiscal, statusVariant(situacaoFiscal)),
      field('Data de inscrição', formatDate(get(fiscal, 'createDate'))),
      field('Data de consulta', formatDate(get(fiscal, 'checkDate'))),
    ]),
    section('Credit Score', [
      field('D00', get(creditScore, 'd00')),
      field('D30', get(creditScore, 'd30')),
      field('D60', get(creditScore, 'd60')),
    ]),
  ];

  return base;
}

function formatGeneric(
  dto: IntegracaoExternaDTO,
  request: unknown,
  response: unknown,
): FormattedIntegration {
  const base = baseFormatted(dto);
  const resp = response as any;
  const statusValue = get(resp, 'status');
  const status = statusValue !== undefined && statusValue !== null ? String(statusValue) : '—';

  base.status = status !== '—' ? { label: status, variant: statusVariant(status) } : undefined;
  base.gridSummary = gridSummary(
    `Tipo ${dto.tipoLabel || dto.tipo}${status !== '—' ? ' — Status: ' + status : ' — Resumo indisponível'}`
  );

  base.sections = [];
  return base;
}
